(function () {
  'use strict';

  if (window.UnifiedProviderManager) {
    return;
  }

  const VERSION = '1.1.0';

  const providers = new Map();

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function getSchema() {
    return (
      window.UnifiedSchema ||
      null
    );
  }

  function getConsent() {
    return (
      window.UnifiedConsent ||
      null
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function isPlainObject(value) {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    );
  }

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  /*
   * ------------------------------------------------------------
   * PERFORMANCE
   * ------------------------------------------------------------
   */

  function performanceStart(
    label,
    meta
  ) {
    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.start ===
        'function'
    ) {
      return window.UnifiedPerformance.start(
        label,
        meta || {}
      );
    }

    return null;
  }

  function performanceEnd(
    tokenId,
    meta
  ) {
    if (
      tokenId === null ||
      typeof tokenId ===
        'undefined'
    ) {
      return null;
    }

    if (
      window.UnifiedPerformance &&
      typeof window.UnifiedPerformance.end ===
        'function'
    ) {
      return window.UnifiedPerformance.end(
        tokenId,
        meta || {}
      );
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * ERROR REPORTING
   * ------------------------------------------------------------
   */

  function reportError(
    severity,
    code,
    message,
    details
  ) {
    if (
      window.UnifiedErrors &&
      typeof window.UnifiedErrors.report ===
        'function'
    ) {
      try {
        return window.UnifiedErrors.report(
          severity,
          code,
          message,
          details || {}
        );
      } catch (error) {
        // Console fallback below.
      }
    }

    const method =
      severity === 'warning'
        ? 'warn'
        : 'error';

    console[method](
      '[Unified Provider Manager] ' +
        message,
      details || {}
    );

    return null;
  }

  /*
   * ------------------------------------------------------------
   * BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        reportError(
          'warning',
          'provider_bus_emit_failed',
          'Provider Manager could not emit through Unified Bus.',
          {
            event_type:
              type,

            error:
              error.message
          }
        );
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                new Date()
                  .toISOString(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * PROVIDER CONFIG
   * ------------------------------------------------------------
   */

  function getProviderConfig(
    providerName
  ) {
    const configured =
      getConfig().providers ||
      {};

    const value =
      configured[
        providerName
      ];

    if (
      value === true ||
      value === false
    ) {
      return {
        enabled:
          value
      };
    }

    if (
      isPlainObject(
        value
      )
    ) {
      return Object.assign(
        {
          enabled:
            value.enabled ===
            true
        },
        value
      );
    }

    return {
      enabled:
        false
    };
  }

  function isProviderEnabled(
    providerName
  ) {
    return (
      getProviderConfig(
        providerName
      ).enabled === true
    );
  }

  /*
   * ------------------------------------------------------------
   * CONSENT
   * ------------------------------------------------------------
   */

  function providerConsentType(
    providerName
  ) {
    /*
     * Analytics providers.
     */

    if (
      providerName === 'ga4' ||
      providerName === 'clarity'
    ) {
      return 'analytics';
    }

    /*
     * Advertising / marketing providers.
     */

    if (
      providerName === 'meta' ||
      providerName === 'tiktok' ||
      providerName === 'linkedin'
    ) {
      return 'marketing';
    }

    /*
     * Custom providers default to analytics.
     */

    return 'analytics';
  }

  function hasRequiredConsent(
    providerName
  ) {
    const config =
      getConfig();

    /*
     * Framework may theoretically operate
     * without consent gating if explicitly
     * configured that way.
     */

    if (
      config.consentRequired !==
      true
    ) {
      return {
        allowed:
          true,

        reason:
          'consent_not_required'
      };
    }

    const consent =
      getConsent();

    /*
     * Fail closed if Consent Manager
     * does not exist.
     */

    if (!consent) {
      return {
        allowed:
          false,

        reason:
          'consent_manager_unavailable'
      };
    }

    /*
     * Fail closed while Shopify privacy
     * state is not ready.
     */

    if (
      typeof consent.isReady !==
        'function' ||
      consent.isReady() !==
        true
    ) {
      return {
        allowed:
          false,

        reason:
          'consent_not_ready'
      };
    }

    const type =
      providerConsentType(
        providerName
      );

    /*
     * Marketing providers.
     */

    if (
      type === 'marketing'
    ) {
      if (
        typeof consent.marketingAllowed !==
          'function'
      ) {
        return {
          allowed:
            false,

          reason:
            'marketing_consent_unavailable'
        };
      }

      /*
       * IMPORTANT:
       *
       * Read consent state ONCE.
       * UnifiedConsent.marketingAllowed()
       * now reads cached Shopify consent state
       * and does not trigger a refresh.
       */

      const allowed =
        consent.marketingAllowed() ===
        true;

      return {
        allowed:
          allowed,

        reason:
          allowed
            ? 'marketing_consent_allowed'
            : 'marketing_consent_denied'
      };
    }

    /*
     * Analytics providers.
     */

    if (
      typeof consent.analyticsAllowed !==
        'function'
    ) {
      return {
        allowed:
          false,

        reason:
          'analytics_consent_unavailable'
      };
    }

    /*
     * Read analytics consent exactly once.
     */

    const allowed =
      consent.analyticsAllowed() ===
      true;

    return {
      allowed:
        allowed,

      reason:
        allowed
          ? 'analytics_consent_allowed'
          : 'analytics_consent_denied'
    };
  }

  /*
   * ------------------------------------------------------------
   * ANALYTICS CONTRACT FILTER
   * ------------------------------------------------------------
   */

  function getAnalyticsMap(
    eventName
  ) {
    const schema =
      getSchema();

    if (
      !schema ||
      typeof schema.getAnalyticsMap !==
        'function'
    ) {
      return {};
    }

    return schema.getAnalyticsMap(
      eventName
    );
  }

  function isAllowedForProvider(
    classification,
    providerName
  ) {
    /*
     * Never leave Unified Tracking.
     */

    if (
      classification ===
        'internal' ||
      classification ===
        'excluded_sensitive' ||
      classification ===
        'unclassified'
    ) {
      return false;
    }

    /*
     * GA4 receives:
     *
     * - GA4 standard fields
     * - analytics dimensions
     * - analytics metrics
     *
     * BigQuery-only identifiers remain internal
     * to the future data architecture.
     */

    if (
      providerName === 'ga4'
    ) {
      return [
        'ga4_standard',
        'dimension',
        'metric'
      ].includes(
        classification
      );
    }

    /*
     * Other providers currently receive only
     * generic dimensions and metrics.
     *
     * Their dedicated adapters may become
     * stricter later.
     */

    return [
      'dimension',
      'metric'
    ].includes(
      classification
    );
  }

  /*
   * ------------------------------------------------------------
   * PAGE LOCATION PRIVACY
   * ------------------------------------------------------------
   */

  function sanitizePageLocation(
    value
  ) {
    if (
      typeof value !==
        'string' ||
      !value
    ) {
      return value;
    }

    /*
     * Strip:
     *
     * - query string
     * - URL fragment/hash
     *
     * Example:
     *
     * /pages/contact?topic=signature
     *
     * becomes:
     *
     * /pages/contact
     */

    try {
      const url =
        new URL(
          value,
          window.location.origin
        );

      return (
        url.origin +
        url.pathname
      );
    } catch (error) {
      return value
        .split('?')[0]
        .split('#')[0];
    }
  }

  /*
   * ------------------------------------------------------------
   * PROVIDER PAYLOAD
   * ------------------------------------------------------------
   */

  function buildProviderPayload(
    providerName,
    eventName,
    payload
  ) {
    if (
      !isPlainObject(
        payload
      )
    ) {
      return {};
    }

    const analyticsMap =
      getAnalyticsMap(
        eventName
      );

    const result = {};

    Object.keys(payload)
      .forEach(
        function (
          fieldName
        ) {
          const classification =
            analyticsMap[
              fieldName
            ];

          /*
           * Schema classification acts
           * as the final analytics allowlist.
           */

          if (
            !isAllowedForProvider(
              classification,
              providerName
            )
          ) {
            return;
          }

          let value =
            payload[
              fieldName
            ];

          /*
           * Query parameters are removed
           * from external page_location.
           */

          if (
            fieldName ===
            'page_location'
          ) {
            value =
              sanitizePageLocation(
                value
              );
          }

          if (
            typeof value ===
              'undefined' ||
            value === null
          ) {
            return;
          }

          /*
           * Provider payloads contain only
           * primitive analytics values.
           */

          if (
            typeof value !==
              'string' &&
            typeof value !==
              'number' &&
            typeof value !==
              'boolean'
          ) {
            return;
          }

          result[
            fieldName
          ] =
            value;
        }
      );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * PROVIDER REGISTRY
   * ------------------------------------------------------------
   */

  function register(
    providerName,
    adapter
  ) {
    const name =
      String(
        providerName || ''
      )
        .trim()
        .toLowerCase();

    if (
      !name ||
      !adapter ||
      typeof adapter.send !==
        'function'
    ) {
      return false;
    }

    providers.set(
      name,
      adapter
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Provider Manager] Provider registered',
        {
          provider:
            name
        }
      );
    }

    return true;
  }

  function unregister(
    providerName
  ) {
    const name =
      String(
        providerName || ''
      )
        .trim()
        .toLowerCase();

    return providers.delete(
      name
    );
  }

  function getRegisteredProviders() {
    return Array.from(
      providers.keys()
    );
  }

  /*
   * ------------------------------------------------------------
   * BUILT-IN GA4 ADAPTER
   * ------------------------------------------------------------
   */

  const ga4Adapter = {
    isReady:
      function () {
        return (
          typeof window.gtag ===
          'function'
        );
      },

    send:
      function (
        eventName,
        payload
      ) {
        if (
          typeof window.gtag !==
          'function'
        ) {
          throw new Error(
            'window.gtag is unavailable'
          );
        }

        window.gtag(
          'event',
          eventName,
          payload
        );

        return true;
      }
  };

  register(
    'ga4',
    ga4Adapter
  );

  /*
   * ------------------------------------------------------------
   * SINGLE PROVIDER DISPATCH
   * ------------------------------------------------------------
   */

  function dispatchToProvider(
    providerName,
    eventName,
    payload
  ) {
    const performanceToken =
      performanceStart(
        'provider.dispatch',
        {
          provider:
            providerName,

          event:
            eventName
        }
      );

    const adapter =
      providers.get(
        providerName
      );

    /*
     * Provider disabled.
     */

    if (
      !isProviderEnabled(
        providerName
      )
    ) {
      const result = {
        provider:
          providerName,

        status:
          'skipped',

        reason:
          'provider_disabled'
      };

      emit(
        'provider_skipped',
        {
          event:
            eventName,

          provider:
            providerName,

          reason:
            result.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'skipped',

          reason:
            result.reason
        }
      );

      return result;
    }

    /*
     * Provider enabled in Config
     * but adapter does not exist.
     */

    if (!adapter) {
      const result = {
        provider:
          providerName,

        status:
          'skipped',

        reason:
          'provider_not_registered'
      };

      emit(
        'provider_skipped',
        {
          event:
            eventName,

          provider:
            providerName,

          reason:
            result.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'skipped',

          reason:
            result.reason
        }
      );

      return result;
    }

    /*
     * Privacy gate.
     */

    const consentResult =
      hasRequiredConsent(
        providerName
      );

    if (
      consentResult.allowed !==
        true
    ) {
      const result = {
        provider:
          providerName,

        status:
          'skipped',

        reason:
          consentResult.reason
      };

      emit(
        'provider_skipped',
        {
          event:
            eventName,

          provider:
            providerName,

          reason:
            result.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'skipped',

          reason:
            result.reason
        }
      );

      return result;
    }

    /*
     * Provider library / runtime readiness.
     */

    if (
      typeof adapter.isReady ===
        'function' &&
      adapter.isReady() !==
        true
    ) {
      const result = {
        provider:
          providerName,

        status:
          'skipped',

        reason:
          'provider_not_ready'
      };

      emit(
        'provider_skipped',
        {
          event:
            eventName,

          provider:
            providerName,

          reason:
            result.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'skipped',

          reason:
            result.reason
        }
      );

      return result;
    }

    /*
     * Final Schema → Provider firewall.
     */

    const providerPayload =
      buildProviderPayload(
        providerName,
        eventName,
        payload
      );

    try {
      adapter.send(
        eventName,
        providerPayload
      );

      const result = {
        provider:
          providerName,

        status:
          'sent',

        reason:
          'provider_sent',

        fields_sent:
          Object.keys(
            providerPayload
          )
      };

      /*
       * Diagnostic event records only
       * field names, never field values.
       */

      emit(
        'provider_sent',
        {
          event:
            eventName,

          provider:
            providerName,

          fields_sent:
            result.fields_sent
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'success'
        }
      );

      if (
        isDebugEnabled()
      ) {
        console.info(
          '[Unified Provider Manager] Event sent',
          {
            event:
              eventName,

            provider:
              providerName,

            fields:
              result.fields_sent
          }
        );
      }

      return result;
    } catch (error) {
      const result = {
        provider:
          providerName,

        status:
          'failed',

        reason:
          'provider_send_failed'
      };

      reportError(
        'error',
        'provider_send_failed',
        'A provider failed while sending an analytics event.',
        {
          event:
            eventName,

          provider:
            providerName,

          error:
            error.message
        }
      );

      emit(
        'provider_failed',
        {
          event:
            eventName,

          provider:
            providerName,

          reason:
            result.reason
        }
      );

      performanceEnd(
        performanceToken,
        {
          provider:
            providerName,

          event:
            eventName,

          status:
            'error',

          reason:
            result.reason
        }
      );

      return result;
    }
  }

  /*
   * ------------------------------------------------------------
   * PROVIDER SELECTION
   * ------------------------------------------------------------
   */

  function resolveProviders(
    routes
  ) {
    const configured =
      getConfig().providers ||
      {};

    /*
     * Explicit routing from Rules Engine.
     */

    if (
      Array.isArray(
        routes
      ) &&
      routes.length
    ) {
      return Array.from(
        new Set(
          routes
            .filter(
              function (
                provider
              ) {
                return (
                  typeof provider ===
                  'string'
                );
              }
            )
            .map(
              function (
                provider
              ) {
                return provider
                  .trim()
                  .toLowerCase();
              }
            )
            .filter(Boolean)
        )
      );
    }

    /*
     * Without explicit routing,
     * evaluate every provider declared
     * in unified-config.js.
     */

    return Object.keys(
      configured
    );
  }

  /*
   * ------------------------------------------------------------
   * MAIN DISPATCH
   * ------------------------------------------------------------
   */

  function dispatch(
    eventName,
    payload,
    options
  ) {
    const settings =
      isPlainObject(
        options
      )
        ? options
        : {};

    if (
      typeof eventName !==
        'string' ||
      !eventName.trim() ||
      !isPlainObject(
        payload
      )
    ) {
      return {
        success:
          false,

        event:
          eventName,

        sent:
          [],

        skipped:
          [],

        failed:
          [],

        results:
          [],

        reason:
          'dispatch_input_invalid'
      };
    }

    const selectedProviders =
      resolveProviders(
        settings.routes
      );

    const results =
      selectedProviders.map(
        function (
          providerName
        ) {
          return dispatchToProvider(
            providerName,
            eventName,
            payload
          );
        }
      );

    const sent =
      results
        .filter(
          function (
            result
          ) {
            return (
              result.status ===
              'sent'
            );
          }
        )
        .map(
          function (
            result
          ) {
            return result.provider;
          }
        );

    const skipped =
      results
        .filter(
          function (
            result
          ) {
            return (
              result.status ===
              'skipped'
            );
          }
        )
        .map(
          function (
            result
          ) {
            return {
              provider:
                result.provider,

              reason:
                result.reason
            };
          }
        );

    const failed =
      results
        .filter(
          function (
            result
          ) {
            return (
              result.status ===
              'failed'
            );
          }
        )
        .map(
          function (
            result
          ) {
            return {
              provider:
                result.provider,

              reason:
                result.reason
            };
          }
        );

    const summary = {
      success:
        failed.length === 0,

      event:
        eventName,

      selected:
        selectedProviders,

      sent:
        sent,

      skipped:
        skipped,

      failed:
        failed,

      results:
        results
    };

    emit(
      'providers_dispatched',
      {
        event:
          eventName,

        selected:
          selectedProviders,

        sent:
          sent,

        skipped:
          skipped,

        failed:
          failed
      }
    );

    return summary;
  }

  /*
   * ------------------------------------------------------------
   * DIAGNOSTICS
   * ------------------------------------------------------------
   */

  function getStatus() {
    const configured =
      getConfig().providers ||
      {};

    const result = {};

    Object.keys(
      configured
    ).forEach(
      function (
        providerName
      ) {
        const adapter =
          providers.get(
            providerName
          );

        const consent =
          hasRequiredConsent(
            providerName
          );

        result[
          providerName
        ] = {
          enabled:
            isProviderEnabled(
              providerName
            ),

          registered:
            Boolean(
              adapter
            ),

          ready:
            Boolean(
              adapter &&
              (
                typeof adapter.isReady !==
                  'function' ||
                adapter.isReady() ===
                  true
              )
            ),

          consent_type:
            providerConsentType(
              providerName
            ),

          consent_allowed:
            consent.allowed,

          consent_reason:
            consent.reason
        };
      }
    );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * PAYLOAD PREVIEW
   * ------------------------------------------------------------
   */

  function previewPayload(
    providerName,
    eventName,
    payload
  ) {
    /*
     * Diagnostic only.
     *
     * Returns exactly what WOULD leave
     * Unified Tracking after Schema filtering.
     *
     * Nothing is dispatched.
     */

    return buildProviderPayload(
      String(
        providerName || ''
      )
        .trim()
        .toLowerCase(),

      eventName,

      payload
    );
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedProviderManager = {
    version:
      VERSION,

    register:
      register,

    unregister:
      unregister,

    dispatch:
      dispatch,

    getStatus:
      getStatus,

    getRegisteredProviders:
      getRegisteredProviders,

    isProviderEnabled:
      isProviderEnabled,

    previewPayload:
      previewPayload,

    buildProviderPayload:
      buildProviderPayload
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Provider Manager] Provider Manager initialized',
      {
        version:
          VERSION,

        registeredProviders:
          getRegisteredProviders(),

        status:
          getStatus()
      }
    );
  }
})();